import { Controller } from 'stimulus';
import formatPrice from '../helpers/formatPrice';
import toArray from '../helpers/toArray';

class ProductDetailController extends Controller {
  connect() {
    this.updatePrices(true);
  }

  updatePrices(initialLoad = false) {
    // Retrieve the selected product variant
    const checkedElement = this.element.querySelector('[data-variant-radio]:checked');

    // Products with a single variant do not have radio buttons to select
    // the variant. Skip updating the prices.
    if (!checkedElement) {
      return;
    }

    // Retrieve the price information for the select product variant
    const priceData = JSON.parse(checkedElement.dataset.pricesData);

    const vatIncluded = window.app.vat_included;

    this.updatePrice(priceData, vatIncluded);

    this.updatePriceSchemes(priceData.schemes, vatIncluded);

    this.updateSupply(priceData.supply);

    this.updatePricePerUnit(priceData, vatIncluded);

    this.updateSeoText(priceData.seo_text);

    if (!initialLoad && checkedElement.dataset.variantUrl) {
      this.retrieveProductVariantPageHtml(checkedElement.dataset.variantUrl)
        .then(this.parseHTML)
        .then(this.updateMeta)
        .then(this.extractDynamicReplaces)
        .then(this.replaceDynamicReplaces)
        .then(() => {
          this.replaceUrl(checkedElement.dataset.variantUrl);
        })
        .catch((err) => {
          throw err;
        });
    }
  }

  updatePricePerUnit(priceData, vatIncluded) {
    if (!priceData.price_per_unit) {
      return;
    }

    this.pricePerUnitTarget.hidden = false;

    this.pricePerUnitTarget.innerHTML = `
    <span>
      Prijs per eenheid
      <span
        class="font-semibold"
        data-price="&euro; ${priceData.price_per_unit}"
        data-price-with-tax="&euro; ${priceData.price_per_unit_with_tax}"
      >
        ${formatPrice(vatIncluded ? priceData.price_per_unit_with_tax : priceData.price_per_unit)}
      </span>
      /m
    </span>`;
  }

  updateSeoText(seoText) {
    if (!seoText) {
      this.seoTextTarget.innerHTML = `
        <div></div>
      `;

      return;
    }

    this.seoTextTarget.innerHTML = `
      <div class="capped-md mx-auto pb-7">
        <div class="cms-content">
          ${seoText}
        </div>
      </div>
    `;
  }

  updatePrice(priceData, vatIncluded) {
    this.priceTarget.innerHTML = formatPrice(
      vatIncluded ? priceData.price_with_tax : priceData.price,
    );
  }

  updatePriceSchemes(schemes, vatIncluded) {
    let schemesHTML = '';

    schemes.forEach((scheme) => {
      schemesHTML += `<div class="mt-2">
        <span>Koop ${scheme.minimum_quantity} voor
          <span
            class="font-semibold color-tertiary"
            data-update-price
            data-price="&euro; ${scheme.price}"
            data-price-with-tax="&euro; ${scheme.price_with_tax}"
          >
            ${formatPrice(vatIncluded ? scheme.price_with_tax : scheme.price)}
          </span>
          per stuk
        </span>
      </div>`;
    });

    this.schemesTarget.innerHTML = schemesHTML;
  }

  updateSupply(supply) {
    this.supplyTarget.innerText = supply;
  }

  onVariantSelected() {
    this.updatePrices();
  }

  /**
   * @param {String} path
   * @returns Promise<String>
   */
  // eslint-disable-next-line class-methods-use-this
  retrieveProductVariantPageHtml(path) {
    return new Promise((resolve, reject) => {
      if (path.indexOf('/p/') !== 0) {
        reject(new Error('Invalid URL given'));
        return;
      }

      const url = `${window.location.origin}${path}`;

      $.ajax({
        url,
      }).then((res) => {
        resolve(res);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  /**
   * @param {String} html
   * @returns Document
   */
  // eslint-disable-next-line class-methods-use-this
  parseHTML(html) {
    return (new DOMParser()).parseFromString(html, 'text/html');
  }

  /**
   * @param {Document} document
   * @returns Document
   */
  // eslint-disable-next-line class-methods-use-this
  updateMeta(sourceDocument) {
    if (sourceDocument.title) {
      document.title = sourceDocument.title;
    }

    return sourceDocument;
  }

  /**
   * @param {Document} document
   * @returns {{[String]: Element}}
   */
  // eslint-disable-next-line class-methods-use-this
  extractDynamicReplaces(document) {
    const obj = {};

    const dynamicReplaceElements = toArray(document.querySelectorAll('[data-dynamic-replace]'));

    dynamicReplaceElements.forEach((element) => {
      obj[element.dataset.dynamicReplace] = element;
    });

    return obj;
  }

  /**
   * @param {{[String]: Element}} dynamicReplaceSources
   */
  // eslint-disable-next-line class-methods-use-this
  replaceDynamicReplaces(dynamicReplaceSources) {
    Object.keys(dynamicReplaceSources).forEach((key) => {
      const sourceElement = dynamicReplaceSources[key];

      const destinationElement = document.querySelector(`[data-dynamic-replace="${key}"]`);

      if (destinationElement) {
        destinationElement.replaceWith(sourceElement);
      }
    });

    return true;
  }

  // eslint-disable-next-line class-methods-use-this
  replaceUrl(url) {
    // Check that the browser supports the History API
    if (typeof window.history !== 'undefined' && typeof window.history.replaceState === 'function') {
      // Check that the URL starts with /p/ to avoid users from setting arbitrary URLs
      if (url.indexOf('/p/') === 0) {
        window.history.replaceState(null, null, url);
      }
    }
  }
}

ProductDetailController.targets = [
  'schemes',
  'price',
  'supply',
  'pricePerUnit',
  'seoText',
];

export default ProductDetailController;
